import React from 'react';
import AffiliationsStepEnum from 'app/blocks/article-affiliations-editor/AffiliationsStepEnum';
import { PriceChangeType } from 'app/blocks/article-affiliations-editor/types';
import { ID, withCodes } from 'app/blocks/common/codes';
import SvgIcon from 'app/blocks/common/svg-icon/svg-icon';
import { MainTitle } from 'app/blocks/titles/titles';

import './header.scss';

type Props = {
    isEmptyAffiliations?: boolean;
    isOnlineOpenJournal?: boolean;
    l: l;
    newAffiliation?: boolean;
    priceChangeType?: PriceChangeType;
    rejectionReasonAccountBalanceNotEnough?: boolean;
    rejectionReasonAccountBalanceNotEnoughInstitutionName?: string;
    step?: Values<typeof AffiliationsStepEnum>;
};
function getEmptyAffiliationsPrompt(l, isEmptyAffiliations, newAffiliation) {
    return isEmptyAffiliations && !newAffiliation ? (
        <div className="EmptyAffiliations-Info" data-seleniumid="affiliation-banner">
            <div className="EmptyAffiliations-Info-Icon">
                {/* eslint-disable-next-line react/jsx-pascal-case */}
                <SvgIcon.info className="InfoIcon" />
            </div>
            <div>{l('ARTICLE_AFFILIATION_EDITOR.EMPTY_AFFILIATIONS_INFO')}</div>
        </div>
    ) : null;
}

function Header({
    isEmptyAffiliations,
    isOnlineOpenJournal,
    l,
    newAffiliation,
    priceChangeType,
    rejectionReasonAccountBalanceNotEnough,
    rejectionReasonAccountBalanceNotEnoughInstitutionName,
    step,
}: Props) {
    let title;
    let text;

    switch (step) {
        case AffiliationsStepEnum.SELECT_PRIMARY_AFFILIATION:
            title = l('ARTICLE_AFFILIATION_EDITOR.SELECT_PRIMARY_AFFILIATION_TITLE');
            text = l('ARTICLE_AFFILIATION_EDITOR.SELECT_PRIMARY_AFFILIATION_TEXT_SINGLE_AUTHOR');
            break;

        case AffiliationsStepEnum.SELECT_AFFILIATION_BY_COUNTRY:
            title = l('ARTICLE_AFFILIATION_EDITOR.SELECT_WOA_INSTITUTION_TITLE');
            text = l('ARTICLE_AFFILIATION_EDITOR.SELECT_WOA_INSTITUTION_TEXT_SINGLE_AUTHOR');
            break;

        case AffiliationsStepEnum.CONFIRM_COUNTRY:
            title = l('ARTICLE_AFFILIATION_EDITOR.CONFIRM_COUNTRY_TITLE');
            break;

        case AffiliationsStepEnum.CONFIRM_PRICE_CHANGE:
            if (
                priceChangeType === PriceChangeType.AUTHOR_TO_FUNDER ||
                priceChangeType === PriceChangeType.FUNDER_TO_FUNDER
            ) {
                title = l('ARTICLE_AFFILIATION_EDITOR.CONFIRM_PRICE_CHANGE_FUNDING_TITLE');
                text = l('ARTICLE_AFFILIATION_EDITOR.CONFIRM_PRICE_CHANGE_FUNDING_TEXT');
            } else if (priceChangeType === PriceChangeType.FUNDER_TO_AUTHOR) {
                if (rejectionReasonAccountBalanceNotEnough) {
                    title = l(
                        'ARTICLE_AFFILIATION_EDITOR.CONFIRM_PRICE_CHANGE_NO_COVERAGE_ACCOUNT_BALANCE_NOT_ENOUGH_TITLE',
                    );
                    text = l(
                        'ARTICLE_AFFILIATION_EDITOR.CONFIRM_PRICE_CHANGE_NO_COVERAGE_ACCOUNT_BALANCE_NOT_ENOUGH_TEXT',
                        { institutionName: rejectionReasonAccountBalanceNotEnoughInstitutionName || '' },
                    );
                } else {
                    title = l('ARTICLE_AFFILIATION_EDITOR.CONFIRM_PRICE_CHANGE_NO_COVERAGE_TITLE');
                    text = l('ARTICLE_AFFILIATION_EDITOR.CONFIRM_PRICE_CHANGE_NO_COVERAGE_TEXT');
                }
            } else if (priceChangeType === PriceChangeType.FUNDER_TO_AUTHOR_INSTITUTIONAL_DISCOUNT) {
                title = l('ARTICLE_AFFILIATION_EDITOR.CONFIRM_PRICE_CHANGE_NO_COVERAGE_TITLE');
                text = l('ARTICLE_AFFILIATION_EDITOR.CONFIRM_PRICE_CHANGE_NEGATIVE_PARTIAL_COVERAGE_TEXT');
            } else if (priceChangeType === PriceChangeType.AUTHOR_TO_AUTHOR_INSTITUTIONAL_DISCOUNT) {
                title = l('ARTICLE_AFFILIATION_EDITOR.CONFIRM_PRICE_CHANGE_NO_COVERAGE_TITLE');
                text = l('ARTICLE_AFFILIATION_EDITOR.CONFIRM_PRICE_CHANGE_POSITIVE_PARTIAL_COVERAGE_TEXT');
            }
            break;

        case AffiliationsStepEnum.AFFILIATION_LIST:
        default:
            title = l(`ARTICLE_AFFILIATION_EDITOR${isEmptyAffiliations ? '.SPECIFY' : '.CONFIRM'}_AFFILIATIONS_TITLE`);
            text = l(
                `ARTICLE_AFFILIATION_EDITOR${isEmptyAffiliations ? '.SPECIFY' : '.CONFIRM'}_AFFILIATIONS_TEXT${
                    isOnlineOpenJournal ? '_ONLINE_OPEN' : '_NON_ONLINE_OPEN'
                }_SINGLE_AUTHOR`,
            );
    }

    return (
        <div className="Header">
            <MainTitle className="Header-Title" data-seleniumid="article-affiliations-title">
                {title}
            </MainTitle>
            {/* eslint-disable-next-line react/no-danger */}
            <p
                className="Header-Text"
                dangerouslySetInnerHTML={{ __html: text }}
                data-seleniumid="article-affiliations-hint"
            />
            {getEmptyAffiliationsPrompt(l, isEmptyAffiliations, newAffiliation)}
        </div>
    );
}

Header.defaultProps = {
    isEmptyAffiliations: false,
    isOnlineOpenJournal: false,
    newAffiliation: undefined,
    priceChangeType: undefined,
    rejectionReasonAccountBalanceNotEnough: undefined,
    rejectionReasonAccountBalanceNotEnoughInstitutionName: undefined,
    step: AffiliationsStepEnum.AFFILIATION_LIST,
};

export { Header };
export default withCodes(Header, ID.ARTICLE_AFFILIATION_EDITOR);
